import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";

export const TURNSTILE_RESPONSE_FIELD = "cf-turnstile-response";

/** Cloudflare dummy keys (1x…, 2x…, 3x…) show "For testing only" — not a widget bug. */
export function isTurnstileTestSiteKey(siteKey) {
  return typeof siteKey === "string" && /^[123]x0+/.test(siteKey);
}

function maskSiteKey(siteKey) {
  if (!siteKey || siteKey.length < 12) return siteKey;
  return `${siteKey.slice(0, 8)}…${siteKey.slice(-4)}`;
}

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { siteKey, onVerify, onExpire, onError, theme = "light", language = "ro" },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
    getToken() {
      if (widgetIdRef.current != null && window.turnstile) {
        return window.turnstile.getResponse(widgetIdRef.current);
      }
      return null;
    },
  }));

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!siteKey) return undefined;

    if (isTurnstileTestSiteKey(siteKey)) {
      console.info(
        `[Turnstile] Test site key (${maskSiteKey(siteKey)}). ` +
          "The 'For testing only' banner is expected. " +
          "Use your real Cloudflare site key from the API to remove it."
      );
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;

        if (widgetIdRef.current != null) {
          window.turnstile.remove(widgetIdRef.current);
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          language,
          callback: (token) => onVerifyRef.current?.(token),
          "expired-callback": () => onExpireRef.current?.(),
          "error-callback": () => onErrorRef.current?.(),
        });
      })
      .catch((err) => {
        console.error("Failed to load Cloudflare Turnstile:", err);
        onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, language]);

  if (!siteKey) return null;

  return <div ref={containerRef} />;
});

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string,
  onVerify: PropTypes.func,
  onExpire: PropTypes.func,
  onError: PropTypes.func,
  theme: PropTypes.oneOf(["light", "dark", "auto"]),
  language: PropTypes.string,
};

export default TurnstileWidget;
